import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Alert, Badge, Button, Progress } from '../ui';
import { ViewState } from '../states';
import { ChapterSection } from './ChapterSection';

/**
 * One opened path: its chapters and their steps (spec §10.4).
 *
 * The progress readout is deliberately two numbers, not one: total completion
 * and required completion. A path whose voluntary depth is finished but whose
 * assigned chapters are not must not be able to look "almost done"
 * (spec §3.3 point 4 — Transfer statt Aktivität).
 */
export function PathDetail({ path, onBack, onToggle, onReload }) {
  const { t } = useTranslation();
  const headingId = useId();

  if (!path) {
    return (
      <ViewState
        status="empty"
        title={t('onboarding.pathNotFound')}
        description={t('onboarding.emptyBody')}
        action={
          <Button variant="secondary" size="compact" onClick={onBack}>
            {t('onboarding.backToOverview')}
          </Button>
        }
      />
    );
  }

  const title = path.titleKey ? t(path.titleKey) : path.title;
  const description = path.descriptionKey ? t(path.descriptionKey) : path.description;
  const { progress } = path;

  return (
    <section className="etch-path" aria-labelledby={headingId} data-ai-generated={path.kind === 'generated' || undefined}>
      <Button variant="ghost" size="compact" className="etch-path__back" onClick={onBack}>
        <ArrowLeft size={16} aria-hidden="true" />
        {t('onboarding.backToOverview')}
      </Button>

      <div className="etch-path__head">
        <h2 className="etch-path__title" id={headingId}>
          {title}
        </h2>
        <div className="etch-path__badges">
          <Badge tone={path.required ? 'brand' : undefined}>
            {path.required ? t('onboarding.required') : t('onboarding.optional')}
          </Badge>
          {path.assigned && (
            <Badge tone="signal">
              <ShieldCheck size={12} aria-hidden="true" /> {t('onboarding.assigned')}
            </Badge>
          )}
          {path.kind === 'generated' && (
            // Art. 50 Abs. 2 AI Act: this whole path (title, chapters, steps)
            // came from GET /api/ckb/onboarding-plan, an LLM plan over the
            // connected knowledge source (model.js buildGeneratedPath()).
            <Badge tone="brand" title={t('onboarding.aiBadgeLabel')}>
              <span aria-hidden="true">{t('onboarding.aiBadge')}</span>
              <span className="etch-visually-hidden">{t('onboarding.aiBadgeLabel')}</span>
            </Badge>
          )}
        </div>
      </div>

      {description && <p className="etch-path__description">{description}</p>}

      <Alert tone="info" title={path.assigned ? t('onboarding.assigned') : t('onboarding.optional')}>
        {path.assigned ? t('onboarding.assignedNote') : t('onboarding.selfMadeNote')}
      </Alert>

      <div className="etch-path-progress">
        <Progress
          value={progress.completed}
          max={Math.max(progress.total, 1)}
          label={t('onboarding.progressOf', { completed: progress.completed, total: progress.total })}
          showValue={false}
        />
        {progress.requiredTotal > 0 && (
          <p className="etch-path-progress__required">
            {t('onboarding.requiredProgress', {
              completed: progress.requiredCompleted,
              total: progress.requiredTotal,
            })}
          </p>
        )}
        <p className="etch-path-progress__note">{t('onboarding.progressNote')}</p>
      </div>

      {path.kind === 'generated' && <p className="etch-path-progress__note">{t('onboarding.planKept')}</p>}

      {path.detailFailed ? (
        // A failed chapter request must not render as an empty path: on an
        // assigned path that would read as "you have nothing to do".
        <ViewState
          compact
          status="error"
          description={t('onboarding.loadFailedBody')}
          onRetry={onReload}
        />
      ) : !path.detailLoaded ? (
        // Chapters load per path: the header, the badges and the progress the
        // overview already knew stay on screen while they arrive.
        <ViewState compact status="partial" description={t('onboarding.loadingHint')} />
      ) : path.chapters.length === 0 ? (
        <ViewState compact status="empty" description={t('onboarding.chapterEmpty')} />
      ) : (
        <ol className="etch-path__chapters">
          {path.chapters.map((chapter) => (
            <li key={chapter.id}>
              <ChapterSection chapter={chapter} onToggle={onToggle} />
            </li>
          ))}
        </ol>
      )}

      {progress.done && progress.total > 0 && (
        <Alert tone="success" title={t('onboarding.stepDone')}>
          {t('onboarding.allDone')}
        </Alert>
      )}
    </section>
  );
}

export default PathDetail;
